import React from "react";
import { motion } from "framer-motion";
import { Apple, Play, ArrowLeft, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

const FEATURES = {
  clara: {
    title: "Clára, your AI health companion",
    tag: "Live in the mobile app",
    pitch: "Voice conversations, camera meal scanning and deep on-device intelligence. Clára is built for your phone's sensors — talk to her, show her your plate, and get instant guidance.",
    points: ["Natural voice chat", "Camera meal scanning", "Context from your day"],
    image: "/clara.jpg",
    fit: "contain",
  },
  meals: {
    title: "Meal tracking that reads your plate",
    tag: "Live in the mobile app",
    pitch: "Barcode scan, voice log and photo meal recognition — logging a meal takes seconds. Full macro and micronutrient breakdowns sync straight to your dashboard.",
    points: ["Barcode & photo scan", "Voice logging", "Macro + micro breakdown"],
    image: "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  workouts: {
    title: "Workout tracking, right at the rack",
    tag: "Live in the mobile app",
    pitch: "Live logging, rest timers and an Apple Watch companion designed for the gym floor. Your training load feeds directly into your recovery and readiness scores.",
    points: ["Live set logging", "Rest timers", "Apple Watch companion"],
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  social: {
    title: "Circles — your health, together",
    tag: "Live in the mobile app",
    pitch: "A private feed, XP, streaks and real-time notifications keep you and your circle accountable. Built for the moments that happen on the go.",
    points: ["Private feed & XP", "Streaks & challenges", "Real-time notifications"],
    image: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
};

function StoreBadge({ icon: Icon, top, bottom, href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
       className="flex items-center gap-3 rounded-md bg-ink px-5 py-2.5 text-white transition-transform hover:-translate-y-0.5"
       data-testid="store-badge">
      <Icon className="h-6 w-6" strokeWidth={1.5} />
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-widest text-white/60">{top}</span>
        <span className="block text-sm font-semibold">{bottom}</span>
      </span>
    </a>
  );
}

export default function DownloadTeaser({ feature }) {
  const f = FEATURES[feature] || FEATURES.clara;
  const appStore = process.env.REACT_APP_APP_STORE_URL;
  const playStore = process.env.REACT_APP_PLAY_STORE_URL;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(appStore)}`;

  return (
    <div className="mx-auto max-w-6xl" data-testid={`teaser-${feature}`}>
      <Link to="/app/dashboard" className="mb-6 inline-flex items-center gap-2 text-sm text-muted hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <div className="grid gap-8 overflow-hidden rounded-lg border border-line bg-card lg:grid-cols-2">
        <div className="flex flex-col justify-center p-8 lg:p-12">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-wash px-3 py-1 text-xs font-semibold text-brand-dark">
            <Smartphone className="h-3.5 w-3.5" /> {f.tag}
          </span>
          <h1 className="mt-5 text-3xl font-bold tracking-tight text-ink sm:text-4xl">{f.title}</h1>
          <p className="mt-4 max-w-md text-muted">{f.pitch}</p>
          <ul className="mt-6 space-y-2">
            {f.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-ink">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" /> {p}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <StoreBadge icon={Apple} top="Download on the" bottom="App Store" href={appStore} />
            <StoreBadge icon={Play} top="Get it on" bottom="Google Play" href={playStore} />
            <div className="flex items-center gap-3 rounded-md border border-line p-2 pr-4">
              <img src={qr} alt="Scan to download" className="h-14 w-14 rounded" data-testid="teaser-qr" />
              <span className="text-xs leading-tight text-muted">Scan to<br />download</span>
            </div>
          </div>
        </div>
        <motion.div className={`relative min-h-[320px] ${f.fit === "contain" ? "bg-white" : "bg-rail"}`}
          initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
          <img src={f.image} alt={f.title} className={`absolute inset-0 h-full w-full ${f.fit === "contain" ? "object-contain" : "object-cover"}`} />
          {f.fit !== "contain" && <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent" />}
        </motion.div>
      </div>
    </div>
  );
}
